import React, { useState } from "react";
import UploadPanel from "./UploadPanel";
import { useAdmin } from "../contexts/AdminContext";

/**
 * Coming-soon overlay — shown when a section has no panorama for the current mode.
 * Embeds the UploadPanel so admins can upload images directly from their computer.
 */
export default function ComingSoon({ section, isNight, onUpdated }) {
    const [open, setOpen] = useState(false);
    const accent = isNight ? "#D4AF37" : "#B89B5E";
    const { isAdmin, setGateOpen } = useAdmin();

    return (
        <div
            data-testid={`coming-soon-${section.id}`}
            className="absolute inset-0 z-10 flex items-center justify-center px-4 py-24 overflow-y-auto"
            style={{
                background: isNight
                    ? "radial-gradient(ellipse at center, rgba(10,10,18,0.88), rgba(0,0,0,0.96) 80%)"
                    : "radial-gradient(ellipse at center, rgba(245,240,228,0.9), rgba(220,210,190,0.96) 80%)",
            }}
        >
            <div
                className="absolute w-[60vmin] h-[60vmin] rounded-full pointer-events-none"
                style={{
                    border: `1px solid ${accent}25`,
                    boxShadow: `0 0 80px ${accent}25 inset, 0 0 120px ${accent}15`,
                }}
            />
            <div
                className="absolute w-[40vmin] h-[40vmin] rounded-full pointer-events-none portal-ring"
                style={{
                    background: `conic-gradient(from 0deg, transparent 0deg, ${accent}55 90deg, transparent 180deg, transparent 270deg, ${accent}33 360deg)`,
                    filter: "blur(30px)",
                    opacity: 0.5,
                }}
            />

            <div className="relative text-center w-full max-w-2xl fade-in">
                <div
                    className="text-[10px] tracking-[0.5em] uppercase mb-5"
                    style={{ color: accent }}
                >
                    {section.label}
                </div>
                <h2
                    className="font-serif-display text-5xl md:text-7xl uppercase tracking-luxe leading-none"
                    style={{ color: isNight ? "#fff" : "#0a0a0a" }}
                >
                    Coming Soon
                </h2>
                <p
                    className="mt-5 text-sm md:text-base font-light tracking-wide"
                    style={{ color: isNight ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.65)" }}
                >
                    Upload 360° panoramas + ISO aerial renders to bring this chamber to life.
                </p>

                {!open ? (
                    <button
                        data-testid={`upload-open-${section.id}`}
                        onClick={() => { if (isAdmin) setOpen(true); else setGateOpen(true); }}
                        className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full text-[11px] tracking-[0.3em] uppercase transition-all hover:scale-[1.03]"
                        style={{
                            background: accent,
                            color: "#0a0a0a",
                            boxShadow: `0 14px 36px -12px ${accent}90`,
                        }}
                    >
                        {isAdmin ? "Upload Your Images" : "Admin · Unlock to Upload"}
                    </button>
                ) : (
                    <div className="mt-8 flex justify-center">
                        <UploadPanel
                            section={section}
                            isNight={isNight}
                            onUpdated={onUpdated}
                            onClose={() => setOpen(false)}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
